import AppProtocol from "@/utils/appProtocol";
import { useGlobalStore, useUserStore } from "@/stores/index";
import { RouteLocationNormalizedGeneric } from "vue-router";
import Router from "@/router";
/**
 * 检查对象是否为空
 * @param obj - 要检查的对象
 * @returns 如果对象为空，返回 true；否则返回 false
 */
function isEmpty(obj: any): boolean {
  if (typeof obj == "undefined" || (!obj && typeof obj != "undefined" && obj != 0) || obj == null) {
    return true;
  }
  for (let i in obj) {
    return false;
  }
  if (typeof obj === "number") {
    return false;
  }
  return true;
}

/**
 * 检查对象是否不为空
 * @param obj - 要检查的对象
 * @returns 如果对象不为空，返回 true；否则返回 false
 */
function isNotEmpty(obj: any): boolean {
  return !isEmpty(obj);
}

/**
 * 获取 URL 参数
 * @param name - 参数名
 * @param url - URL 字符串
 * @returns 参数值
 */
function getUrlParameterByName(name: string, url: string): string | null {
  if (!url) {
    return "";
  }
  name = name.replace(/[\[\]]/g, "\\$&");
  let regex = new RegExp("[?&]" + name + "(=([^&#]*)|&|#|$)"),
    results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return "";
  return decodeURIComponent(results[2].replace(/\+/g, " "));
}

/**
 * 解析查询字符串
 * @param query - 查询字符串
 * @returns 解析后的对象
 */
function parseQuery(query: string): Record<string, string> {
  var reg = /([^=&?\s]+)[=\s]*([^&\s]*)/g;
  var obj = {};
  while (reg.exec(query)) {
    obj[RegExp.$1] = RegExp.$2;
  }
  return obj;
}

/**
 * 拼接 URL 参数
 * @param obj - 参数对象
 * @returns 拼接后的 URL 参数字符串
 */
function urlParamCombine(obj: Record<string, any>): string {
  let param = "?";
  for (const key in obj) {
    obj[key] = obj[key] == null ? "" : obj[key];
    if (Array.isArray(obj[key]) || typeof obj[key] === "object") {
      for (const k in obj[key]) {
        param += `${k}=${obj[key][k]}&`;
      }
    } else {
      param += `${key}=${obj[key]}&`;
    }
  }
  return param.slice(0, -1);
}

/**
 * 拼接 URL 参数，首字母升序排序
 * @param queryParams - 查询参数对象
 * @returns 排序后的查询字符串
 */
function concatSortParams(queryParams: Record<string, any>): string {
  const sortKeys = allKeysSorted(queryParams);
  const queryParis: string[] = [];
  for (const key of sortKeys) {
    queryParis.push(`${key}=${queryParams[key]}`);
  }
  return queryParis.join("&");
}

/**
 * 获取字典所有 key，并按首字母升序排序
 * @param dic - 字典对象
 * @returns 排序后的 key 数组
 */
function allKeysSorted(dic: Record<string, any>): string[] {
  return Object.keys(dic).sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
}

/**
 * JSON 字符串转换为对象实体
 * @param str - JSON 字符串
 * @returns 对象
 */
function stringToObject(str: string): Record<string, any> {
  if (isEmpty(str)) {
    return {};
  }
  return JSON.parse(decodeURIComponent(str));
}

/**
 * 对象转换为 JSON 字符串
 * @param obj - 对象
 * @returns JSON 字符串
 */
function objectToString(obj: Record<string, any>): string {
  if (isEmpty(obj)) {
    return "";
  }
  return encodeURIComponent(JSON.stringify(obj));
}
/**
 * 深拷贝（假定不存在undefine和函数对象）
 * @param src - 源对象
 * @returns 深拷贝后的对象
 */
function deepCopy<T>(src: T): T {
  const cache = new Map();
  function _deepClone(value) {
    if (typeof value !== "object" || value === null) {
      return value;
    }
    if (cache.has(value)) {
      return cache.get(value);
    }
    const result = Array.isArray(value) ? [] : {};
    cache.set(value, result);
    for (const key in value) {
      result[key] = _deepClone(value[key]);
    }
    return result;
  }
  return _deepClone(src);
}

/**
 * 移除字符串中的 emoji 及相关组合字符
 * @param value - 原始字符串
 * @returns 过滤后的字符串
 */
function removeEmoji(value: string): string {
  return Array.from(value)
    .filter((char) => {
      const codePoint = char.codePointAt(0) || 0;
      return !(
        (codePoint >= 0x1f000 && codePoint <= 0x1ffff) ||
        (codePoint >= 0x2600 && codePoint <= 0x27bf) ||
        (codePoint >= 0xfe00 && codePoint <= 0xfe0f) ||
        codePoint === 0x200d ||
        codePoint === 0x20e3 ||
        codePoint === 0x00a9 ||
        codePoint === 0x00ae ||
        codePoint === 0x2122
      );
    })
    .join("");
}

/**
 * 获取随机数
 * @param min - 开始范围
 * @param max - 结束范围
 * @returns 随机数
 */
function randomRange(min: number, max: number): number | undefined {
  if (typeof min !== "number" || typeof max !== "number") return;
  return Math.floor(Math.random() * (max - min + 1) + min);
}

/**
 * 数组去重
 * @param arr - 数组
 * @returns 去重后的数组
 */
function toSetUniq<T>(arr: T[]): T[] {
  return Array.from(new Set(arr));
}

/**
 * 防抖函数
 * @param fn - 执行函数
 * @param time - 时间间隔
 * @param triggerNow - 是否立即执行
 * @returns 防抖后的函数
 */
function debounce(fn: (...args: any[]) => void, time: number, triggerNow?: boolean): () => void {
  let t: NodeJS.Timeout | null = null;

  return function () {
    const _self = this;
    const args = arguments;

    if (t) {
      clearTimeout(t);
    }

    if (triggerNow) {
      const exec = !t;

      t = setTimeout(function () {
        t = null;
      }, time);

      if (exec) {
        fn.apply(_self, args);
      }
    } else {
      t = setTimeout(function () {
        fn.apply(_self, args);
        t = null;
      }, time);
    }
  };
}

/**
 * 获取详细的渠道号
 * @returns 渠道号
 */
function getCHCode(): string {
  let CHCode = "";
  const ua = getUserAgent().toLowerCase();
  if (/miniprogram/gi.test(ua)) {
    CHCode = "Miniprogram";
  } else if (/android/gi.test(ua)) {
    CHCode = "Android";
  } else if (/iphone/gi.test(ua)) {
    CHCode = "iOS";
  } else {
    CHCode = "h5";
  }
  return CHCode;
}

/**
 * 节流函数
 * @param func - 执行函数
 * @param wait - 时间间隔
 * @param options - 选项
 * @returns 节流后的函数
 */
function throttle(func: (...args: any[]) => void, wait: number, options?: { leading?: boolean; trailing?: boolean }): () => void {
  let context: any, args: any, result: any;
  let timeout: NodeJS.Timeout | null = null;
  let previous = 0;
  if (!options) options = {};
  const later = function () {
    previous = options.leading === false ? 0 : new Date().getTime();
    timeout = null;
    result = func.apply(context, args);
    if (!timeout) context = args = null;
  };
  return function () {
    const now = new Date().getTime();
    if (!previous && options.leading === false) previous = now;
    const remaining = wait - (now - previous);
    context = this;
    args = arguments;
    if (remaining <= 0 || remaining > wait) {
      if (timeout) {
        clearTimeout(timeout);
        timeout = null;
      }
      previous = now;
      result = func.apply(context, args);
      if (!timeout) context = args = null;
    } else if (!timeout && options.trailing !== false) {
      timeout = setTimeout(later, remaining);
    }
    return result;
  };
}

/**
 * 获取浏览器用户代理
 * @returns 用户代理字符串
 */
function getUserAgent(): string {
  let ua = "";
  try {
    if (typeof navigator !== "undefined" && navigator) {
      ua = navigator.userAgent || "";
    }
  } catch (e) {
    ua = "";
  }
  return ua;
}

/**
 * 获取设备环境
 * @returns 设备环境对象
 */
function getEnvironment(): {
  isAndroid: boolean;
  isHarmony: boolean;
  isIos: boolean;
  isMobile: boolean;
  isTablet: boolean;
  isPhone: boolean;
  isPCminiprogram: boolean;
} {
  const ua = getUserAgent();
  const isAndroid = /(?:Android)/.test(ua);
  const isHarmony = /(?:HarmonyOS|Harmony)/.test(ua);
  const isFireFox = /(?:Firefox)/.test(ua);
  const isTablet = /(?:iPad|PlayBook|Tablet)/.test(ua) || (isAndroid && !/(?:Mobile)/.test(ua)) || (isFireFox && /(?:Tablet)/.test(ua));
  const isPhone = /(?:iPhone)/.test(ua) && !isTablet;
  const isPCminiprogram = /miniprogramenv\/windows/gi.test(ua) || /miniprogramenv\/mac/gi.test(ua);

  const isIos = /ip(ad|hone|od)/i.test(ua);
  const isMobile = isAndroid || isIos || isHarmony;

  return {
    isAndroid,
    isHarmony,
    isIos,
    isMobile,
    isTablet,
    isPhone,
    isPCminiprogram,
  };
}

/**
 * 获取 app 的版本号
 * @returns 版本号
 */

function getAppVersion(): number {
  const ua = getUserAgent().toLowerCase();
  const i = ua.lastIndexOf("kernel");
  const version = i > -1 ? parseInt(ua.substring(i).replace("kernel", "")) : 0; //初始版本号10000
  return version;
}

/**
 * 获取当前浏览器的协议版本号
 * @returns 协议版本号
 */
function getKernel(): number {
  let userAgent_arr = navigator.userAgent.split("/");
  let client_kernell = userAgent_arr[userAgent_arr.length - 1];
  let kernell = client_kernell.replace("Kernel", "");
  if (navigator.userAgent.includes("Kernel1300")) {
    // console.log('返回1300，兼容奇妙屋iOS某些版本拼接多个kernel的问题');
    return 1300;
  }
  if (kernell && client_kernell.includes("Kernel")) {
    return parseInt(kernell);
  }
  if ((window.AndroidService && typeof window.AndroidService.openProtocol === "function") || (window.activity && typeof window.activity.openProtocol === "function")) {
    return 1300;
  }
  return 1000;
}

/**
 * 判断是否在 app 环境中
 * @returns 是否在 app 环境中
 */
function isInApp(): boolean {
  const ua = getUserAgent().toLowerCase();
  if (ua.indexOf("babybus") > -1 || ua.indexOf("kernel") > -1) {
    return true;
  }
  return false;
}

/**
 * 判断是否在微信小程序环境中
 * @returns 是否在微信小程序环境中
 */
function isInMiniProgram(): boolean {
  const ua = getUserAgent().toLowerCase();
  if (ua.indexOf("miniprogram") > -1) {
    return true;
  }

  return false;
}

/**
 * 判断是否为对象
 * @param obj - 要检查的对象
 * @returns 是否为对象
 */
function isObject(obj: any): boolean {
  return obj !== null && typeof obj === "object";
}

/**
 * 对象转字符串
 * @param obj - 对象
 * @returns 字符串
 */
function serialize(obj: any): string {
  if (typeof obj == "object") {
    return JSON.stringify(obj);
  } else {
    return obj;
  }
}

/**
 * 字符串转对象
 * @param strVal - 字符串
 * @returns 对象
 */
function deserialize(strVal: string | object): any {
  if (typeof strVal === "object") {
    return strVal;
  }
  let val: any;
  try {
    val = JSON.parse(strVal);
  } catch (error) {
    console.error("util中字符串转对象方法捕捉到的错误", error);
    val = strVal;
  }
  return val;
}

/**
 * 解析查询字符串
 * @param url - URL 字符串
 * @returns 解析后的对象
 */
function parseQueryString(url: string): Record<string, string> {
  const obj: Record<string, string> = {};
  const keyValuePairs = url.substring(url.indexOf("?") + 1).split("&");
  for (const keyValue of keyValuePairs) {
    const [key, value] = keyValue.split("=");
    obj[key] = value;
  }
  return obj;
}

/**
 * 判断是否为数字
 * @param obj - 要检查的值
 * @returns 是否为数字
 */
function isNumber(obj: any): boolean {
  return obj === +obj;
}

/**
 * 复制到剪贴板
 * @param text - 要复制的文本
 */
function copyClick(text: string): void {
  const body = document.body;
  const input = document.createElement("input");
  body.append(input);
  input.style.opacity = "0";
  input.value = text;
  input.select();
  input.setSelectionRange(0, input.value.length);
  document.execCommand("Copy");
  input.blur();
  input.remove();
}

/**
 * 判断活动状态
 * @param beginTime - 开始时间
 * @param endTime - 结束时间
 * @param serverTime - 服务器时间
 * @returns 活动状态码
 */
function judgeActivityStatus(beginTime: number, endTime: number, serverTime: number): number {
  if (!beginTime || !endTime || !serverTime) {
    console.log("活动状态有问题，不显示入口");
    return 3;
  }
  if (serverTime < beginTime) {
    console.log("预热状态");
    return 0;
  } else if (serverTime >= beginTime && serverTime <= endTime) {
    console.log("活动进行时");
    return 1;
  } else {
    console.log("活动已结束");
    return 2;
  }
}

/**
 * toFixed 精度修正，如3.565.toFixed(2)结果为3.56，修正后为3.57
 * @param number - 需要被四舍五入的数字
 * @param m - 保留小数位数
 * @returns 四舍五入后的字符串
 *
 */
function toFixed(number: number, m: number = 0): string {
  number = parseFloat(number.toString());
  if (typeof number !== "number" || isNaN(number)) {
    throw new Error("number不是数字");
  }

  const factor = Math.pow(10, m);
  const intValue = Math.round(number * factor);

  return (intValue / factor).toString();
}

/**
 * 银行家舍入法（Banker's Rounding）
 * 一种特殊的舍入策略，主要用于减少舍入操作中的累积误差。
 * 与传统的四舍五入不同，它在处理中间值（如.5）时更加公平，
 * 避免了始终向上或始终向下舍入导致的系统性偏差。
 *
 * 规则:
 * 1. 如果小数部分小于0.5，则向下舍入（与标准四舍五入相同）
 * 2. 如果小数部分大于0.5，则向上舍入（与标准四舍五入相同）
 * 3. 如果小数部分等于0.5，则舍入到最接近的偶数
 *    - 例如：2.5 舍入为 2（向下至偶数）
 *    - 例如：3.5 舍入为 4（向上至偶数）
 * 注意：如果5后面还有非0数，则按“大于5”处理，直接进位
 *    - 例如：28.5454 舍入为 28.55，28.545 舍入为 28.54
 *
 * @param {number} num - 需要舍入的数值
 * @param {number} [decimalPlaces=0] - 保留的小数位数
 * @returns {number} 舍入后的结果
 */
function bankerRound(num: number, decimalPlaces: number = 0): number {
  if (!Number.isFinite(num)) return num;

  const factor = Math.pow(10, decimalPlaces);
  const shifted = num * factor;
  const sign = shifted < 0 ? -1 : 1;
  const absShifted = Math.abs(shifted);
  const floored = Math.floor(absShifted);
  const fraction = absShifted - floored;
  const epsilon = 1e-10;

  if (fraction < 0.5 - epsilon) {
    return (sign * floored) / factor;
  }

  if (fraction > 0.5 + epsilon) {
    return (sign * (floored + 1)) / factor;
  }

  // 小数部分恰好为 .5 时，按银行家舍入：看整数部分奇偶
  const roundedAbs = floored % 2 === 0 ? floored : floored + 1;
  return (sign * roundedAbs) / factor;
}

/**
 * 固定字体大小
 * @param size - 基准大小
 */
function fixedFontSize(size: number = 1080): void {
  function resizeFun(): void {
    const htmlEle = window.document.documentElement;
    const width = Math.min(htmlEle.clientWidth, htmlEle.clientHeight, 600);
    htmlEle.style.fontSize = (width / size) * 100 + "px";
  }
  resizeFun();
  window.addEventListener("resize", resizeFun, false);
}

/**
 * 统一调用登录
 * @param params - 登录参数
 **/
function toLogin(params?: RouteLocationNormalizedGeneric): void {
  localStorage.setItem("isLoginExecutedRecently", "1");
  const store = useGlobalStore();
  const user = useUserStore();
  const historyStack = store.$state.historyStack;
  const loginRouteList = [];
  for (let historyItem of historyStack) {
    loginRouteList.push({
      name: historyItem.name,
      path: historyItem.path,
      query: historyItem.query,
      params: historyItem.params,
    });
  }
  if (typeof params === "object" && params.path) {
    loginRouteList.push({
      name: params.name,
      path: params.path,
      query: params.query,
      params: params.params,
    });
    sessionStorage.setItem("app_navigatorAfterLogin", JSON.stringify(params));
  }
  localStorage.setItem("loginRouteList", JSON.stringify(loginRouteList));

  //未登录返回 删除登录相关缓存
  var visibleListener = (e: Event): void => {
    if (document.visibilityState === "visible") {
      document.removeEventListener("visibilitychange", visibleListener);
      setTimeout(() => {
        if (localStorage.getItem("isLoginExecutedRecently") === "1" && document.visibilityState === "visible") {
          localStorage.removeItem("isLoginExecutedRecently");
          localStorage.removeItem("loginRouteList");
          sessionStorage.removeItem("app_navigatorAfterLogin");
        }

        if (!user.$state.isLogin && !historyStack.length) {
          Router.replace({
            name: "index",
          });
        }
      }, 1000);
    }
  };
  document.addEventListener("visibilitychange", visibleListener);

  if (isInApp()) {
    AppProtocol.toLogin();
  } else if (isInMiniProgram()) {
    wx.miniProgram.navigateTo({
      url: `/pages/user-login/index/index?from=webview`,
    });
  }
}

/**
 * 设置页面标题
 * @param title - 页面标题
 **/
function setPageTitle(title: string): void {
  if (typeof title !== "string") return;
  document.title = title || "小布咕商城";
}

/**
 * 手机号中间4位改为*
 * @param phoneNumber - 手机号
 * @returns - 遮罩后的手机号
 **/
function maskPhoneNumber(phoneNumber: string): string {
  // 确保手机号是有效的
  const regex = /^(\d{3})(\d{4})(\d{4})$/;
  const match = phoneNumber.match(regex);

  if (match) {
    // 使用正则表达式替换中间的四位数为星号
    return `${match[1]}****${match[3]}`;
  } else {
    // 如果手机号格式不正确，返回原手机号或抛出错误
    return "手机号格式不正确";
  }
}

/**
 * 生成 UUID v4
 * @returns UUID 字符串
 */
export function generateUUID(): string {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c == "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * 检查文件大小是否符合限制
 * @param file 要检查的文件
 * @param maxSize 最大文件大小，可以是数字（字节）或字符串（如 '10MB', '5KB'）
 * @returns 如果文件大小在限制内返回 true，否则返回 false
 */
const isFileSizeValid = (file: File, maxSize: number | string): boolean => {
  let maxSizeInBytes: number;

  if (typeof maxSize === "number") {
    // 如果是数字，直接作为字节大小
    maxSizeInBytes = maxSize;
  } else if (typeof maxSize === "string") {
    // 如果是字符串，解析单位
    const units = maxSize.match(/^(\d+(?:\.\d+)?)\s*(MB|M|KB|GB|B)?$/i);
    if (!units) {
      throw new Error('Invalid size format. Use formats like "10MB", "5KB", "1GB", or "1024B"');
    }

    const value = parseFloat(units[1]);
    const unit = (units[2] || "B").toUpperCase();

    switch (unit) {
      case "B":
        maxSizeInBytes = value;
        break;
      case "KB":
        maxSizeInBytes = value * 1024;
        break;
      case "MB":
      case "M":
        maxSizeInBytes = value * 1024 * 1024;
        break;
      case "GB":
        maxSizeInBytes = value * 1024 * 1024 * 1024;
        break;
      default:
        maxSizeInBytes = value;
    }
  } else {
    throw new Error("maxSize must be a number or string");
  }

  return file.size <= maxSizeInBytes;
};

/**
 * 精确求和（支持加减），按 2 位小数精度
 * 支持两种调用：
 *  - sum(0.1, 0.2, -0.3)
 *  - sum([0.1, 0.2, -0.3])
 */
function sum(...args: Array<number | string>): number;
function sum(values: Array<number | string>): number;
function sum(first: any, ...rest: any[]): number {
  const decimalPlaces = 2;
  const factor = Math.pow(10, decimalPlaces);

  const values: Array<number | string> = Array.isArray(first) ? first : [first, ...rest];

  let totalInt = 0; // 按 10^2 放大后的整数累加
  for (const v of values) {
    const n = Number(v);
    if (!Number.isFinite(n)) continue; // 跳过无效值
    totalInt += Math.round(n * factor);
  }
  // 再缩小并按 2 位小数四舍五入
  return Number((totalInt / factor).toFixed(decimalPlaces));
}

/**
 * 判断是否是快速点击
 * @param timeout
 * @returns
 */
const isFastClick = (timeout: number = 200) => {
  let clickTime = 0;

  return () => {
    const current = Date.now();

    if (!clickTime) {
      clickTime = current;
      return false;
    }

    const timeDiff = current - clickTime;
    if (timeDiff < timeout) {
      console.warn("检测到快速点击");
      clickTime = current;
      return true;
    }

    clickTime = current;
    return false;
  };
};

/**
 * 获取图片自定义样式
 * @param customStyle 自定义样式对象（可选宽度/高度）
 * @returns 包含宽度高度的样式对象
 */
const getImageCustomStyle = (customStyle: { outlineWidth?: string; outlineHeight?: string }) => {
  const { outlineWidth, outlineHeight } = customStyle || {};
  if (outlineWidth && outlineHeight) {
    return `${(6.7 / Number(outlineWidth)) * Number(outlineHeight)}rem`;
  }
  return "";
};

export default {
  isEmpty,
  isNotEmpty,
  getUrlParameterByName,
  parseQuery,
  getEnvironment,
  urlParamCombine,
  objectToString,
  stringToObject,
  deepCopy, //深复制
  removeEmoji,
  randomRange, //随机数
  toSetUniq, //数组去重
  debounce,
  throttle,
  isObject,
  concatSortParams,
  serialize,
  deserialize,
  getUserAgent,
  parseQueryString,
  isNumber,
  copyClick,
  isInApp,
  getAppVersion,
  getKernel,
  toFixed,
  judgeActivityStatus,
  fixedFontSize,
  isInMiniProgram,
  getCHCode,
  toLogin,
  setPageTitle,
  maskPhoneNumber,
  bankerRound,
  generateUUID,
  isFileSizeValid,
  sum,
  isFastClick,
  getImageCustomStyle,
};
